/* eslint-disable wc/no-self-class */
import { html, unsafeCSS, PropertyValues } from 'lit';
import { query, property } from 'lit/decorators.js';
import { Swiper } from 'swiper';
import { Autoplay, Pagination, EffectFade } from 'swiper/modules';
import { BaseElement, customElement, watch } from '../base-element';
import styles from './carousel.scss?inline';

@customElement('ps-carousel')
export class CarouselWC extends BaseElement {
  static styles = unsafeCSS(styles);

  @query('.swiper')
  private swiperContainer?: HTMLDivElement;

  @property({ reflect: true, type: Boolean })
  autoplay? = false;

  swiper?: Swiper;

  private initSwiper(): void {
    if (!this.swiper) {
      this.swiper = new Swiper(this.swiperContainer as HTMLElement, {
        loop: false,
        // Moving content stays still for people who ask for reduced motion.
        autoplay:
          this.autoplay &&
          !window.matchMedia('(prefers-reduced-motion: reduce)').matches
            ? {
                delay: 3000,
                disableOnInteraction: false,
              }
            : false,
        slidesPerView: 1,
        cssMode: false,
        keyboard: false,
        mousewheel: false,
        // this custom swiperElementNodeName prop is a temp patch / workaround until PR to swiper (removing the hard-coded `SWIPER-CONTAINER` tagname) is merged
        swiperElementNodeName: 'PS-CAROUSEL',
        spaceBetween: 0,
        centeredSlides: true,
        resizeObserver: false,
        allowTouchMove: false,
        initialSlide: 0,
        effect: 'fade',
        fadeEffect: { crossFade: true },
        pagination: {
          el: '.swiper-pagination',
          clickable: false,
          type: 'bullets',
          renderBullet(index, className) {
            return (
              `<span class="${className}">` +
              `<em>${index}</em>` +
              `<i></i>` +
              `<b></b>` +
              `</span>`
            );
          },
        },
        modules: [Autoplay, EffectFade, Pagination],
      });
    }
  }

  firstUpdated(changedProperties: PropertyValues) {
    super.firstUpdated(changedProperties);
    setTimeout(() => {
      this.initSwiper();
    }, 50);
  }

  disconnectedCallback() {
    if (this.swiper) {
      this.swiper.destroy();
      this.swiper = undefined;
    }
    // eslint-disable-next-line wc/guard-super-call
    super.disconnectedCallback();
  }

  render() {
    return html`
      <div class="c-carousel swiper">
        <div class="c-carousel__wrapper swiper-wrapper" part="carousel-wrapper">
          <slot></slot>
        </div>
        <div class="swiper-pagination"></div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'ps-carousel': CarouselWC;
  }
  enum PSElementTagNameMap {
    'ps-carousel' = 'ps-carousel',
  }
}
